/**
 * GreenSide News — Multilingual & Inclusive Publishing demo data.
 *
 * EVERYTHING here is SAMPLE data for a front-end demonstration. No translation
 * API, model or backend is connected. Translations are prewritten by hand and
 * labelled as examples. Nothing here collects, stores or infers real reader
 * data — and no individual is ever categorised by race or ethnicity.
 */

/* ------------------------------------------------------------------ */
/* Languages                                                           */
/* ------------------------------------------------------------------ */

export type LangCode = 'en' | 'es';

export type LanguageMeta = {
  code: LangCode;
  label: string;
  native: string;
  status: 'live' | 'planned';
};

export const LANGUAGES: LanguageMeta[] = [
  { code: 'en', label: 'English', native: 'English', status: 'live' },
  { code: 'es', label: 'Spanish', native: 'Español', status: 'live' },
];

/** Languages shown as a future roadmap only — not translated yet. */
export const PLANNED_LANGUAGES = ['Chinese', 'Vietnamese', 'Korean', 'Tagalog'];

export function languageLabel(code: LangCode): string {
  return LANGUAGES.find((l) => l.code === code)?.label ?? code;
}

/* ------------------------------------------------------------------ */
/* Translation editorial stages                                        */
/* ------------------------------------------------------------------ */

export type TranslationStage = 'Not started' | 'Draft' | 'In review' | 'Approved' | 'Published';

export const TRANSLATION_STAGES: TranslationStage[] = [
  'Not started',
  'Draft',
  'In review',
  'Approved',
  'Published',
];

/** The end-to-end multilingual workflow stage names (editorial concept). */
export const WORKFLOW_STAGES = [
  'Incoming story',
  'Original language',
  'Translation draft',
  'Editorial review',
  'Approved language versions',
  'Publication preview',
];

/* ------------------------------------------------------------------ */
/* Sample translated stories                                           */
/* ------------------------------------------------------------------ */

export type LangVersion = {
  headline: string;
  summary: string;
  body: string;
};

export type MultilingualStory = {
  id: string;
  slug: string;
  category: string;
  regionId: string;
  author: string;
  image: string;
  originalLang: LangCode;
  versions: Partial<Record<LangCode, LangVersion>>;
  stages: Partial<Record<LangCode, TranslationStage>>;
  translator: Partial<Record<LangCode, string>>;
};

export const MULTILINGUAL_STORIES: MultilingualStory[] = [
  {
    id: 'ml-01',
    slug: 'long-beach-junior-open-expands-to-36-holes',
    category: 'Tournament Coverage',
    regionId: 'los-angeles',
    author: 'Marisol Vega',
    originalLang: 'en',
    image:
      'https://readdy.ai/api/search-image?query=Sunlit%20junior%20golf%20tournament%20on%20a%20well%20kept%20public%20course%20with%20players%20and%20flags%20in%20the%20distance%2C%20deep%20green%20editorial%20sports%20photography%2C%20soft%20morning%20light%2C%20clean%20uncluttered%20composition%2C%20no%20text&width=1600&height=900&seq=gs-ml-lbjr&orientation=landscape',
    versions: {
      en: {
        headline: 'Long Beach Junior Open Expands to 36 Holes After Record Entries',
        summary: 'A record 214 junior entries pushed the Long Beach Junior Open to a two-day, 36-hole format for 2026.',
        body: `EL DORADO PARK — What began as a quiet Sunday-morning tradition has outgrown its bracket. With 214 junior golfers on the entry list, the Long Beach Junior Open will run two full days for the first time in its nineteen-year history.

Tournament director Ray Ortiz said the expansion was a response rather than a choice. "We had to turn families away last year for the first time," he said. "Adding the second round lets us keep everyone who qualified on the course instead of on a waitlist."

Proceeds again benefit the Long Beach Youth Sports Fund, which has covered entry fees for more than 400 local juniors over the past decade.`,
      },
      es: {
        headline: 'El Long Beach Junior Open se amplía a 36 hoyos tras un récord de inscripciones',
        summary:
          'Un récord de 214 inscripciones juveniles llevó al Long Beach Junior Open a un formato de dos días y 36 hoyos para 2026.',
        body: `EL DORADO PARK — Lo que comenzó como una tranquila tradición de domingo por la mañana ha superado su propio cuadro. Con 214 golfistas juveniles en la lista de inscritos, el Long Beach Junior Open se disputará durante dos días completos por primera vez en sus diecinueve años de historia.

El director del torneo, Ray Ortiz, afirmó que la ampliación fue una respuesta más que una elección. «El año pasado tuvimos que rechazar a familias por primera vez», dijo. «Añadir la segunda ronda nos permite mantener en el campo a todos los que se clasificaron, en lugar de dejarlos en una lista de espera».

Los ingresos vuelven a beneficiar al Long Beach Youth Sports Fund, que ha cubierto las cuotas de inscripción de más de 400 jóvenes locales durante la última década.`,
      },
    },
    stages: { en: 'Published', es: 'In review' },
    translator: { es: 'AI-assisted draft · reviewed by M. Vega' },
  },
  {
    id: 'ml-02',
    slug: 'women-in-golf-league-doubles-in-san-diego',
    category: 'Community',
    regionId: 'san-diego',
    author: 'Devon Rios',
    originalLang: 'en',
    image:
      'https://readdy.ai/api/search-image?query=Group%20of%20women%20golfers%20smiling%20together%20on%20a%20coastal%20course%20putting%20green%20with%20friendly%20warm%20tone%2C%20bright%20editorial%20lifestyle%20sports%20photography%2C%20soft%20afternoon%20light%2C%20clean%20composition%2C%20no%20text&width=1600&height=900&seq=gs-ml-women&orientation=landscape',
    versions: {
      en: {
        headline: 'Women in Golf League Doubles Its San Diego Membership',
        summary: 'A Saturday women-led league has doubled in size in one season, adding two new course partners.',
        body: `SAN DIEGO — A women-led weekend league that started with a dozen players now counts more than sixty members across the county, organisers said this week.

The league runs on a rotating schedule across four public courses and pairs newer players with experienced mentors. Two more courses have agreed to host rounds this winter.

"We built this so nobody has to show up alone," said founder Angela Moss. "You sign up, you're paired, and you have a group by the first tee."`,
      },
      es: {
        headline: 'La liga Mujeres en el Golf duplica su membresía en San Diego',
        summary:
          'Una liga de los sábados liderada por mujeres duplicó su tamaño en una temporada y sumó dos campos asociados.',
        body: `SAN DIEGO — Una liga de fin de semana liderada por mujeres que comenzó con una docena de jugadoras ahora cuenta con más de sesenta miembros en todo el condado, según informaron sus organizadoras esta semana.

La liga funciona con un calendario rotativo en cuatro campos públicos y empareja a jugadoras nuevas con mentoras con experiencia. Dos campos más han aceptado albergar rondas este invierno.

«Creamos esto para que nadie tenga que aparecer sola», dijo la fundadora, Angela Moss. «Te inscribes, te emparejan y ya tienes un grupo en el primer tee».`,
      },
    },
    stages: { en: 'Published', es: 'Approved' },
    translator: { es: 'AI-assisted draft · reviewed by the newsroom' },
  },
  {
    id: 'ml-03',
    slug: 'sonoran-courses-prepare-for-peak-winter-play',
    category: 'Course News',
    regionId: 'tucson',
    author: 'GreenSide Newsroom',
    originalLang: 'en',
    image:
      'https://readdy.ai/api/search-image?query=Sonoran%20desert%20golf%20course%20with%20rolling%20fairways%20and%20distant%20mountains%20in%20Tucson%20under%20soft%20early%20light%2C%20editorial%20landscape%20photography%2C%20calm%20and%20clean%2C%20no%20text&width=1600&height=900&seq=gs-ml-sonoran&orientation=landscape',
    versions: {
      en: {
        headline: 'Sonoran Courses Prepare for Peak Winter Play',
        summary: 'Tucson-area courses are adding maintenance windows ahead of the busiest stretch of the calendar.',
        body: `TUCSON — Sonoran-area courses are preparing for peak winter play, adding maintenance windows and expanding cart availability ahead of the busiest stretch of the calendar.

Two clubs have also introduced a new twilight rate aimed at newer players.`,
      },
    },
    stages: { en: 'Published', es: 'Not started' },
    translator: {},
  },
];

export function storyBySlug(slug: string): MultilingualStory | undefined {
  return MULTILINGUAL_STORIES.find((s) => s.slug === slug);
}

/* ------------------------------------------------------------------ */
/* Inclusive community coverage categories                             */
/* ------------------------------------------------------------------ */

export type InclusiveCategory = {
  id: string;
  label: string;
  icon: string;
  description: string;
  sampleCount: number;
};

export const INCLUSIVE_CATEGORIES: InclusiveCategory[] = [
  { id: 'community-orgs', label: 'Community golf organizations', icon: 'ri-community-line', description: 'Local clubs, leagues and volunteer groups that keep the game running.', sampleCount: 18 },
  { id: 'charity', label: 'Charity tournaments', icon: 'ri-hand-heart-line', description: 'Fundraising events and the beneficiaries they support.', sampleCount: 24 },
  { id: 'youth', label: 'Youth golf', icon: 'ri-seedling-line', description: 'Junior programs, clinics and school golf coverage.', sampleCount: 21 },
  { id: 'women', label: 'Women in golf', icon: 'ri-women-line', description: 'Women-led leagues, events and player stories.', sampleCount: 15 },
  { id: 'cultural', label: 'Cultural golf associations', icon: 'ri-global-line', description: 'Associations formed around shared heritage or community.', sampleCount: 9 },
  { id: 'spotlights', label: 'Local player spotlights', icon: 'ri-user-star-line', description: 'Profiles of everyday golfers, volunteers and organisers.', sampleCount: 27 },
];

/** Shown beside the categories to make the editorial stance explicit. */
export const CATEGORY_INTEGRITY_NOTE =
  'Categories describe programs, events and organizations — never people. Readers and players are not automatically sorted by race, ethnicity or any other sensitive characteristic.';

/* ------------------------------------------------------------------ */
/* Reader preferences (independent of one another)                     */
/* ------------------------------------------------------------------ */

export const READER_REGIONS: { id: string; label: string }[] = [
  { id: 'all', label: 'All regions' },
  { id: 'los-angeles', label: 'Los Angeles' },
  { id: 'orange-county', label: 'Orange County' },
  { id: 'inland-empire', label: 'Inland Empire' },
  { id: 'san-diego', label: 'San Diego' },
  { id: 'phoenix', label: 'Phoenix' },
  { id: 'tucson', label: 'Tucson' },
];

export const NEWSLETTER_INTERESTS: { id: string; label: string }[] = [
  { id: 'tournaments', label: 'Tournaments & events' },
  { id: 'course-news', label: 'Course news' },
  { id: 'charity', label: 'Charity & fundraising' },
  { id: 'youth', label: 'Youth golf' },
  { id: 'women', label: 'Women in golf' },
  { id: 'community-orgs', label: 'Community organizations' },
  { id: 'sponsors', label: 'Sponsors & offers' },
];

/* ------------------------------------------------------------------ */
/* Audience analytics — preferred language & translation coverage      */
/* ------------------------------------------------------------------ */

export type LanguageStat = { lang: string; pct: number };

/** Preferred language distribution (sample). Readers self-select; never inferred. */
export const LANGUAGE_DISTRIBUTION: LanguageStat[] = [
  { lang: 'English', pct: 78 },
  { lang: 'Spanish', pct: 17 },
  { lang: 'Not specified', pct: 5 },
];

export const SUBSCRIBERS_BY_LANGUAGE: { lang: string; count: number }[] = [
  { lang: 'English', count: 1840 },
  { lang: 'Spanish', count: 410 },
  { lang: 'Not specified', count: 95 },
];

export type EngagementRow = { lang: string; pageViews: number; sessions: number; avgReadSeconds: number };

/** Content engagement by language (sample). */
export const ENGAGEMENT_BY_LANGUAGE: EngagementRow[] = [
  { lang: 'English', pageViews: 412000, sessions: 186000, avgReadSeconds: 96 },
  { lang: 'Spanish', pageViews: 68000, sessions: 31000, avgReadSeconds: 88 },
  { lang: 'Not specified', pageViews: 12000, sessions: 5400, avgReadSeconds: 61 },
];

export type NewsletterRow = { lang: string; sent: number; openRate: number; clickRate: number };

/** Newsletter performance by language (sample). */
export const NEWSLETTER_BY_LANGUAGE: NewsletterRow[] = [
  { lang: 'English', sent: 11500, openRate: 42.6, clickRate: 6.1 },
  { lang: 'Spanish', sent: 2600, openRate: 48.9, clickRate: 7.4 },
  { lang: 'Not specified', sent: 640, openRate: 33.2, clickRate: 4.0 },
];

export type CoverageRow = { regionId: string; stories: number; translated: number };

/** Translation coverage by market (sample). */
export const TRANSLATION_COVERAGE: CoverageRow[] = [
  { regionId: 'los-angeles', stories: 120, translated: 64 },
  { regionId: 'orange-county', stories: 86, translated: 41 },
  { regionId: 'inland-empire', stories: 54, translated: 22 },
  { regionId: 'san-diego', stories: 92, translated: 47 },
  { regionId: 'phoenix', stories: 71, translated: 30 },
  { regionId: 'tucson', stories: 48, translated: 15 },
];

export type ReadershipRow = { regionId: string; sessions: number; spanishShare: number };

/** Regional readership with the share of Spanish-language sessions (sample). */
export const READERSHIP_BY_REGION: ReadershipRow[] = [
  { regionId: 'los-angeles', sessions: 84000, spanishShare: 24 },
  { regionId: 'orange-county', sessions: 44000, spanishShare: 19 },
  { regionId: 'inland-empire', sessions: 29500, spanishShare: 31 },
  { regionId: 'san-diego', sessions: 47000, spanishShare: 22 },
  { regionId: 'phoenix', sessions: 35000, spanishShare: 26 },
  { regionId: 'tucson', sessions: 20800, spanishShare: 33 },
];

function coveragePct(row: CoverageRow): number {
  if (!row.stories) return 0;
  return Math.round((row.translated / row.stories) * 100);
}

export function translationCoveragePct(row: CoverageRow): number {
  return coveragePct(row);
}

export const TOTAL_STORIES = TRANSLATION_COVERAGE.reduce((a, r) => a + r.stories, 0);
export const TOTAL_TRANSLATED = TRANSLATION_COVERAGE.reduce((a, r) => a + r.translated, 0);

export function overallCoveragePct(): number {
  if (!TOTAL_STORIES) return 0;
  return Math.round((TOTAL_TRANSLATED / TOTAL_STORIES) * 100);
}

/* ------------------------------------------------------------------ */
/* Multilingual editorial queue (management preview)                   */
/* ------------------------------------------------------------------ */

export type MultilingualQueueItem = {
  id: string;
  headline: string;
  category: string;
  regionId: string;
  originalLang: LangCode;
  source: string;
  received: string;
  targetLang: LangCode;
  stage: TranslationStage;
  translator: string;
};

export const MULTILINGUAL_QUEUE: MultilingualQueueItem[] = [
  { id: 'mq-1', headline: 'Long Beach Junior Open Expands to 36 Holes', category: 'Tournament Coverage', regionId: 'los-angeles', originalLang: 'en', source: 'Community submission', received: '2026-10-08', targetLang: 'es', stage: 'In review', translator: 'AI-assisted draft · awaiting editor review' },
  { id: 'mq-2', headline: 'Coastal Courses Report Strongest Fall Bookings', category: 'Course News', regionId: 'san-diego', originalLang: 'en', source: 'Reader tip', received: '2026-10-07', targetLang: 'es', stage: 'Draft', translator: 'AI-assisted draft · not yet reviewed' },
  { id: 'mq-3', headline: 'Valley Veterans Cup Raises Record Total', category: 'Charity', regionId: 'inland-empire', originalLang: 'en', source: 'AI discovery', received: '2026-10-09', targetLang: 'es', stage: 'Not started', translator: 'Translation not requested yet' },
  { id: 'mq-4', headline: 'Women in Golf League Doubles Its San Diego Membership', category: 'Community', regionId: 'san-diego', originalLang: 'en', source: 'Staff draft', received: '2026-10-06', targetLang: 'es', stage: 'Approved', translator: 'AI-assisted draft · approved by the newsroom' },
];

export function regionLabelFromId(id: string): string {
  return READER_REGIONS.find((r) => r.id === id)?.label ?? id;
}